<template>
    <div class="tooltip-wrapper" :class="{ 'has-tooltip': showTooltipIcon }" @click.stop="showTooltipIcon ? toggleTooltip() : null">
        <span class="cost-label">
            {{ label }}
            <svg
                v-if="showTooltipIcon"
                class="info-icon-svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
            >
                <path d="M8.0026 14.6654C11.6693 14.6654 14.6693 11.6654 14.6693 7.9987C14.6693 4.33203 11.6693 1.33203 8.0026 1.33203C4.33594 1.33203 1.33594 4.33203 1.33594 7.9987C1.33594 11.6654 4.33594 14.6654 8.0026 14.6654Z" stroke="#929292" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 5.33203V8.66536" stroke="#929292" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 10.668H8.00599" stroke="#929292" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </span>

        <div
            v-if="hasContent"
            class="tooltip-content"
            :class="{
                'is-visible': tooltipVisible
            }"
        >
            <div class="tooltip-header">
                <h4>{{ title }}</h4>
                <svg
                    class="close-icon"
                    @click.stop="closeTooltip"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </div>
            <div class="tooltip-body">
                <table class="breakdown-table">
                    <thead>
                        <tr>
                            <th>Fee</th>
                            <th>Qty</th>
                            <th>Amt</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(item, index) in filteredBreakdown"
                            :key="index"
                        >
                            <td>{{ item.name || item.service_name || "-" }}</td>
                            <td>
                                {{
                                    item.type === "sales_tax" ||
                                    item.name === "Sales Tax" ||
                                    item.type === "shipping_method" ||
                                    item.name === "Shipping Method Charge" ||
                                    item.name === "Shipping Charge"
                                        ? "-"
                                        : item.quantity || item.qty || "-"
                                }}
                            </td>
                            <td>
                                {{
                                    item.type === "sales_tax" ||
                                    item.name === "Sales Tax" ||
                                    item.type === "shipping_method" ||
                                    item.name === "Shipping Method Charge" ||
                                    item.name === "Shipping Charge"
                                        ? "-"
                                        : (item.amt || item.amount) &&
                                          item.amt !== "INCL" &&
                                          item.amt !== "-" &&
                                          item.amount !== "INCL" &&
                                          item.amount !== "-"
                                        ? formatPrice(
                                              parseFloat(
                                                  item.amt || item.amount || 0
                                              )
                                          )
                                        : "-"
                                }}
                            </td>
                            <td>
                                {{
                                    item.total &&
                                    item.total !== "INCL" &&
                                    item.total !== "-"
                                        ? formatPrice(
                                              parseFloat(item.total || 0)
                                          )
                                        : (item.amt || item.amount) &&
                                          item.amt !== "INCL" &&
                                          item.amt !== "-" &&
                                          item.amount !== "INCL" &&
                                          item.amount !== "-"
                                        ? formatPrice(
                                              parseFloat(
                                                  item.amt || item.amount || 0
                                              )
                                          )
                                        : "-"
                                }}
                            </td>
                        </tr>
                        <tr
                            v-if="
                                filteredBreakdown.length > 0 &&
                                calculatedTotal > 0
                            "
                            class="total-row"
                        >
                            <td colspan="3"><strong>Total</strong></td>
                            <td>
                                <strong>{{
                                    formatPrice(calculatedTotal)
                                }}</strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "PendingPaymentTooltip",
    props: {
        label: {
            type: String,
            required: true
        },
        title: {
            type: String,
            default: "Breakdown"
        },
        breakdown: {
            type: Array,
            default: () => []
        },
        total: {
            type: Number,
            default: 0
        },
        formatPrice: {
            type: Function,
            required: true
        },
        excludeTypes: {
            type: Array,
            default: () => []
        },
        excludeNames: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            tooltipVisible: false,
            tooltipId: null
        };
    },
    computed: {
        hasContent() {
            return this.filteredBreakdown && this.filteredBreakdown.length > 0;
        },
        showTooltipIcon() {
            return this.hasContent;
        },
        filteredBreakdown() {
            if (!this.breakdown || !Array.isArray(this.breakdown)) return [];
            // Return all items if no exclusions specified
            if (
                this.excludeTypes.length === 0 &&
                this.excludeNames.length === 0
            ) {
                return this.breakdown;
            }
            // Filter out excluded types and names
            return this.breakdown.filter((item) => {
                // Filter by type
                if (this.excludeTypes.length > 0 && item.type) {
                    if (this.excludeTypes.includes(item.type)) {
                        return false;
                    }
                }
                // Filter by name
                if (this.excludeNames.length > 0) {
                    const itemName = item.name || item.service_name || "";
                    if (
                        this.excludeNames.some((excludedName) =>
                            itemName
                                .toLowerCase()
                                .includes(excludedName.toLowerCase())
                        )
                    ) {
                        return false;
                    }
                }
                return true;
            });
        },
        calculatedTotal() {
            if (!this.filteredBreakdown || this.filteredBreakdown.length === 0)
                return 0;
            let total = 0;
            this.filteredBreakdown.forEach((item) => {
                if (item.total && item.total !== "INCL" && item.total !== "-") {
                    total += parseFloat(item.total) || 0;
                } else if (
                    (item.amt || item.amount) &&
                    item.amt !== "INCL" &&
                    item.amt !== "-" &&
                    item.amount !== "INCL" &&
                    item.amount !== "-"
                ) {
                    total += parseFloat(item.amt || item.amount || 0);
                }
            });
            return parseFloat(total.toFixed(2));
        }
    },
    mounted() {
        // Generate unique ID for this tooltip
        this.tooltipId =
            "tooltip-" +
            Date.now() +
            "-" +
            Math.random().toString(36).substr(2, 9);

        // Listen for close events from other tooltips
        const closeHandler = (excludeId) => {
            if (this.tooltipId !== excludeId) {
                this.tooltipVisible = false;
            }
        };

        this.$root.$on("close-all-tooltips", closeHandler);
        this._closeHandler = closeHandler;

        // Close tooltip when clicking outside
        document.addEventListener("click", this.closeTooltipOnClickOutside);
    },
    beforeDestroy() {
        // Clean up event listener
        if (this._closeHandler) {
            this.$root.$off("close-all-tooltips", this._closeHandler);
        }
        // Remove click outside listener
        document.removeEventListener("click", this.closeTooltipOnClickOutside);
    },
    methods: {
        toggleTooltip() {
            this.$root.$emit("close-all-tooltips", this.tooltipId);
            this.tooltipVisible = !this.tooltipVisible;
        },
        closeTooltip() {
            this.tooltipVisible = false;
        },
        closeTooltipOnClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.tooltipVisible = false;
            }
        }
    }
};
</script>

<style scoped>
.tooltip-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
}
.tooltip-wrapper.has-tooltip {
    cursor: pointer;
}

.tooltip-wrapper .cost-label {
    display: inline-flex;
    align-items: center;
    font-size: 16px;
}

.tooltip-wrapper .info-icon-svg {
    width: 16px;
    height: 16px;
    margin-left: 5px;
    flex-shrink: 0;
}

.tooltip-wrapper:hover .info-icon-svg path {
    stroke: #555;
}

.tooltip-content {
    visibility: hidden;
    position: absolute;
    top: 100%;
    right: 0;
    transform: translateX(0);
    background: #fff;
    color: hsla(0, 0%, 7%, 1);
    padding: 0px;
    border-radius: 6px;
    z-index: 9999;
    min-width: 392px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    margin-top: 8px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: normal;
    font-size: 14px;
}

.tooltip-content.is-visible {
    visibility: visible;
    opacity: 1;
}

.tooltip-header {
    padding: 9px 16px;
    background: hsla(204, 63%, 94%, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}

.tooltip-header h4 {
    font-weight: 600;
    font-size: 14px;
    line-height: 14px;
    color: hsla(209, 72%, 41%, 1);
    margin-bottom: 0px;
}

.tooltip-header .close-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: #666;
    flex-shrink: 0;
    transition: color 0.2s ease;
}

.tooltip-header .close-icon:hover {
    color: #333;
}

.tooltip-body {
    white-space: normal;
    padding: 16px;
    max-height: 180px;
    overflow-y: auto;
}

.breakdown-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 12px;
}

.breakdown-table thead {
    background-color: #f5f5f5;
}

.breakdown-table th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
    line-height: 1.4;
}

.breakdown-table th:nth-child(2),
.breakdown-table th:nth-child(3),
.breakdown-table th:nth-child(4) {
    text-align: right;
}

.breakdown-table td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-weight: normal;
    font-size: 12px;
    color: #333;
    line-height: 1.4;
}

.breakdown-table td:nth-child(2),
.breakdown-table td:nth-child(3),
.breakdown-table td:nth-child(4) {
    text-align: right;
}

.breakdown-table tbody tr {
    border-bottom: 1px solid #eee;
}

.breakdown-table tbody tr.total-row {
    border-top: 2px solid #ddd;
    border-bottom: none !important;
    background-color: #f5f5f5 !important;
    font-weight: bold;
}

.breakdown-table tbody tr.total-row td {
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: none !important;
}
</style>

